import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Helmet } from 'react-helmet';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const SystemOfEquationsCalculator = () => {
  const [equation1, setEquation1] = useState({
    a: '',
    b: '',
    c: ''
  });
  const [equation2, setEquation2] = useState({
    a: '',
    b: '',
    c: ''
  });
  const [solution, setSolution] = useState(null);
  const [error, setError] = useState('');

  const handleInputChange = (e, equation) => {
    const { name, value } = e.target;
    if (equation === 1) {
      setEquation1(prev => ({
        ...prev,
        [name]: value
      }));
    } else if (equation === 2) {
      setEquation2(prev => ({
        ...prev,
        [name]: value
      }));
    }
    setError('');
  };

  const solveSystem = () => {
    const { a: a1, b: b1, c: c1 } = equation1;
    const { a: a2, b: b2, c: c2 } = equation2;

    // Validate input
    if (!a1 || !b1 || !c1 || !a2 || !b2 || !c2) {
      setError('Please enter all coefficients');
      return;
    }

    if (isNaN(a1) || isNaN(b1) || isNaN(c1) || isNaN(a2) || isNaN(b2) || isNaN(c2)) {
      setError('Please enter valid numbers for all coefficients');
      return;
    }

    // Convert to numbers
    const numericA1 = parseFloat(a1);
    const numericB1 = parseFloat(b1);
    const numericC1 = parseFloat(c1);
    const numericA2 = parseFloat(a2);
    const numericB2 = parseFloat(b2);
    const numericC2 = parseFloat(c2);

    // Check for parallel or coincident lines
    if (numericA1 / numericA2 === numericB1 / numericB2) {
      if (numericA1 / numericA2 === numericC1 / numericC2) {
        setError('The equations are coincident and have infinitely many solutions.');
        return;
      } else {
        setError('The equations are parallel and have no solution.');
        return;
      }
    }

    // Solve the system using substitution or elimination
    const x = (numericC1 * numericB2 - numericC2 * numericB1) / (numericA1 * numericB2 - numericA2 * numericB1);
    const y = (numericC2 * numericA1 - numericC1 * numericA2) / (numericA1 * numericB2 - numericA2 * numericB1);

    setSolution({
      x: x.toFixed(2),
      y: y.toFixed(2)
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.5 }}
      className="min-h-screen bg-gradient-to-r from-purple-100 to-pink-100"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Helmet>
          <title>System of Equations Calculator</title>
          <meta name="description" content="Solve a system of linear equations using substitution or elimination method." />
          <meta name="keywords" content="system of equations calculator, solve linear equations, substitution method, elimination method" />
        </Helmet>

        <div className="relative overflow-hidden bg-white rounded-lg shadow-2xl p-8 mb-8">
          <div className="calculator-header flex items-center mb-8">
            <div className="header-icon text-4xl text-pink-600 mr-4">EQ</div>
            <h1 className="text-3xl font-bold text-gray-800">System of Equations Calculator</h1>
          </div>

          <div className="text-gray-600 mb-8">
            <p>Solve a system of linear equations using substitution or elimination method.</p>
          </div>

          <div className="grid grid-cols-1 gap-6 mb-6">
            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Equation 1 (a1x + b1y = c1):</label>
              <div className="flex space-x-4">
                <input
                  type="text"
                  name="a"
                  value={equation1.a}
                  onChange={(e) => handleInputChange(e, 1)}
                  className="w-1/3 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                  placeholder="a1"
                />
                <input
                  type="text"
                  name="b"
                  value={equation1.b}
                  onChange={(e) => handleInputChange(e, 1)}
                  className="w-1/3 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                  placeholder="b1"
                />
                <input
                  type="text"
                  name="c"
                  value={equation1.c}
                  onChange={(e) => handleInputChange(e, 1)}
                  className="w-1/3 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                  placeholder="c1"
                />
              </div>
            </div>
            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Equation 2 (a2x + b2y = c2):</label>
              <div className="flex space-x-4">
                <input
                  type="text"
                  name="a"
                  value={equation2.a}
                  onChange={(e) => handleInputChange(e, 2)}
                  className="w-1/3 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                  placeholder="a2"
                />
                <input
                  type="text"
                  name="b"
                  value={equation2.b}
                  onChange={(e) => handleInputChange(e, 2)}
                  className="w-1/3 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                  placeholder="b2"
                />
                <input
                  type="text"
                  name="c"
                  value={equation2.c}
                  onChange={(e) => handleInputChange(e, 2)}
                  className="w-1/3 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                  placeholder="c2"
                />
              </div>
            </div>
          </div>

          <button
            onClick={solveSystem}
            className="w-full py-3 px-4 bg-pink-600 text-white font-medium rounded-lg hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2 transition-colors duration-200"
          >
            Solve System
          </button>

          {error && (
            <div className="mt-4 p-4 text-red-700 bg-red-100 rounded-lg">
              {error}
            </div>
          )}

          {solution && (
            <div className="mt-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Solution</h2>
              
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-700 mb-4">Values of Variables</h3>
                <p className="text-lg">x = {solution.x}</p>
                <p className="text-lg">y = {solution.y}</p>
              </div>
            </div>
          )}
        </div>

        {/* How to Use Section */}
        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">How to Use</h2>
          <div className="text-gray-600">
            <p>Follow these steps to use the System of Equations Calculator:</p>
            <ol className="list-decimal pl-6 mt-4">
              <li><strong>Enter Coefficients:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Input the coefficients a1, b1, c1 for the first equation (a1x + b1y = c1).</li>
                  <li>Input the coefficients a2, b2, c2 for the second equation (a2x + b2y = c2).</li>
                </ul>
              </li>
              <li><strong>Click Solve:</strong> The calculator will determine:
                <ul className="list-disc pl-6 mt-2">
                  <li>The values of x and y that satisfy both equations.</li>
                </ul>
              </li>
              <li><strong>Interpret Results:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Review the calculated values of x and y.</li>
                </ul>
              </li>
            </ol>
            <div className="mt-4 p-4 bg-blue-100 rounded-lg">
              <p><strong>💡 Pro Tips:</strong></p>
              <ul className="list-disc pl-6 mt-2">
                <li>Ensure all coefficients are valid numbers.</li>
                <li>Use this calculator for solving systems of linear equations in algebra, physics, or any scenario involving two variables.</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Example Calculation Section */}
        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Example Calculation</h2>
          <div className="text-gray-600">
            <p>Here's an example calculation for the system of equations:</p>
            
            <div className="bg-white p-6 rounded-lg shadow-md mt-4">
              <h4 className="mb-4">Sample Data:</h4>
              <ul className="list-disc pl-6 mb-4">
                <li>Equation 1: 2x + 3y = 12</li>
                <li>Equation 2: 4x - y = 2</li>
              </ul>

              <h4 className="mb-4">Calculation Method:</h4>
              <ol className="list-decimal pl-6 mb-4">
                <li>Use the elimination method to solve for x and y
                  <ul className="list-disc pl-6">
                    <li>Multiply the second equation by 3 to align the coefficients of y: 12x - 3y = 6</li>
                    <li>Add the modified second equation to the first equation: 14x = 18</li>
                    <li>Solve for x: x = 18 / 14 = 1.29</li>
                    <li>Substitute x back into one of the original equations to solve for y: 2(1.29) + 3y = 12</li>
                    <li>3y = 12 - 2.58 = 9.42</li>
                    <li>y = 9.42 / 3 = 3.14</li>
                  </ul>
                </li>
              </ol>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">FAQ</h2>
          <div className="text-gray-600">
            <p>Here are some frequently asked questions about the System of Equations Calculator:</p>
            <div className="mt-4 space-y-4">
              <div className="p-4 bg-gray-50 rounded-lg shadow-inner">
                <strong>Q: What is a system of equations?</strong>
                <p>A system of equations is a set of two or more equations with the same variables. The solution is the set of values that satisfy all equations simultaneously.</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg shadow-inner">
                <strong>Q: How do I use the calculator?</strong>
                <p>Enter the coefficients of the two equations and click "Solve System" to find the values of the variables that satisfy both equations.</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg shadow-inner">
                <strong>Q: What if the equations are parallel or coincident?</strong>
                <p>If the equations are parallel, they have no solution. If they are coincident, they have infinitely many solutions. The calculator will indicate these cases accordingly.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default SystemOfEquationsCalculator;